import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { useActionPermission } from "@/modules/auth/presentation/model/useActionPermission";
import { Breadcrumb } from "@/shared/ui/Breadcrumb";
import type {
  CreateProductPayload,
  LinkedModifierRow,
  VariantRow,
} from "@/modules/catalog/domain/types";
import { uploadImageToKey } from "@/shared/lib/upload-image-to-key";
import { toast } from "sonner";
import { confirm } from "@/shared/lib/confirm";
import { FormRoot, FormInput, FormTextarea, RHF, zodResolver } from "@/shared/forms";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";



import { useNavigate, useParams } from "@tanstack/react-router";
import {
  ArrowLeftIcon,
  GripVerticalIcon,
  ListIcon,
  PencilIcon,
  Trash2Icon,
} from "lucide-react";
import { useEffect, useState } from "react";
import { z } from "zod";
import {
  useProductLaunchesQuery,
  useUpsertProductLaunch,
} from "@/modules/membership/presentation/api/queries";
import {
  useAddModifierLink,
  useCreateVariant,
  useModifiersQuery,
  useProductFullQuery,
  useRemoveModifierLink,
  useRemoveVariant,
  useReorderModifierLinks,
  useUpdateProduct,
  useUpdateVariant,
} from "../api/queries";
import { LinkModifierSetModal } from "../ui/LinkModifierSetModal";
import {
  PRODUCT_IMAGE_KEY_PREFIX,
  ProductImageUploadField,
} from "../ui/ProductImageUploadField";
import { VariantFormModal } from "../ui/VariantFormModal";

const GeneralFormSchema = z.object({
  type: z.enum(["MENU", "RETAIL"]),
  name: z.string().min(1, "ຕ້ອງໃສ່ຊື່ສິນຄ້າ"),
  description: z.string().nullable().optional(),
  imageUrl: z.string().nullable().optional(),
  isActive: z.boolean(),
});

type GeneralFormValues = z.infer<typeof GeneralFormSchema>;

function formatPriceLak(n: number): string {
  return new Intl.NumberFormat("en-US").format(n);
}

export function ProductEditPage() {
  const { id } = useParams({ from: "/app/catalog/products/$id/edit" });
  const nav = useNavigate({ from: "/app/catalog/products/$id/edit" });
  const productId = id ?? "";

  const product = useProductFullQuery(productId);
  const modifiersQuery = useModifiersQuery();
  const updateProduct = useUpdateProduct(productId);
  const createVariant = useCreateVariant(productId);
  const [editingVariantId, setEditingVariantId] = useState<string>("");
  const updateVariant = useUpdateVariant(productId, editingVariantId);
  const removeVariant = useRemoveVariant(productId);
  const canEditRecipe = useActionPermission(["inventory:read"]);
  const addModifierLink = useAddModifierLink(productId);
  const removeModifierLink = useRemoveModifierLink(productId);
  const reorderModifierLinks = useReorderModifierLinks(productId);
  const upsertProductLaunch = useUpsertProductLaunch();
  const variantIds = (product.data?.variants ?? []).map((variant) => variant.id);
  const productLaunchesQuery = useProductLaunchesQuery(variantIds);

  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);
  const [variantModal, setVariantModal] = useState<{
    open: boolean;
    variant: VariantRow | null;
  }>({ open: false, variant: null });
  const [linkModifierOpen, setLinkModifierOpen] = useState(false);

  const generalForm = RHF.useForm<GeneralFormValues>({
    resolver: zodResolver(GeneralFormSchema),
    defaultValues: {
      type: "MENU",
      name: "",
      description: null,
      imageUrl: null,
      isActive: true,
    },
  });

  const p = product.data;
  const launchByVariantId = new Map(
    (productLaunchesQuery.data?.launches ?? []).map((launch) => [
      launch.variantId,
      launch,
    ]),
  );

  useEffect(() => {
    if (!p) return;
    generalForm.reset({
      type: p.type,
      name: p.name,
      description: p.description ?? null,
      imageUrl: p.imageUrl ?? null,
      isActive: p.isActive,
    });
  }, [p, generalForm.reset]);

  const onSaveGeneral = generalForm.handleSubmit(async (values) => {
    let imageUrl = values.imageUrl ?? null;
    if (pendingImageFile) {
      imageUrl = await uploadImageToKey(
        PRODUCT_IMAGE_KEY_PREFIX,
        pendingImageFile,
      );
      setPendingImageFile(null);
    }
    const payload: CreateProductPayload = {
      type: values.type,
      name: values.name,
      description: values.description ?? null,
      imageUrl,
      isActive: values.isActive,
    };
    await updateProduct.mutateAsync(payload);
    toast.success("ບັນທຶກການປ່ຽນແປງສຳເລັດ");
  });

  const openAddVariant = () => {
    setEditingVariantId("");
    setVariantModal({ open: true, variant: null });
  };
  const openEditVariant = (v: VariantRow) => {
    setEditingVariantId(v.id);
    setVariantModal({ open: true, variant: v });
  };

  const moveModifier = (index: number, direction: 1 | -1) => {
    if (!p?.linkedModifiers?.length) return;
    const next = [...p.linkedModifiers];
    const j = index + direction;
    if (j < 0 || j >= next.length) return;
    const a = next[index];
    const b = next[j];
    if (!a || !b) return;
    next[index] = b;
    next[j] = a;
    const items = next.map((m, i) => ({
      modifierId: m.modifierId,
      sortOrder: i,
    }));
    reorderModifierLinks.mutate({ items });
  };

  if (product.isLoading || !p) {
    return (
      <>
        <Header>
          <Breadcrumb
            items={[
              {
                label: "ສິນຄ້າ / ເມນູ",
                to: "/app/catalog/products",
              },
              {
                label: "ແກ້ໄຂສິນຄ້າ",
              },
            ]}
          />
        </Header>
        <Main>
          <div className="text-muted-foreground">
            {product.isLoading ? "ກຳລັງໂຫຼດ..." : "ບໍ່ພົບສິນຄ້າ"}
          </div>
        </Main>
      </>
    );
  }

  return (
    <>
      <Header>
        <Breadcrumb
          items={[
            {
              label: "ສິນຄ້າ / ເມນູ",
              to: "/app/catalog/products",
            },
            {
              label: "ແກ້ໄຂສິນຄ້າ",
            },
          ]}
        />
      </Header>
      <Main>
        {/* Toolbar: title + breadcrumb + ກັບຄືນ, ຍົກເລີກ, ບັນທຶກການປ່ຽນແປງ */}
        <div className="mb-6 flex flex-wrap items-center justify-between gap-2">
          <div>
            <h2 className="font-bold text-2xl tracking-tight">ແກ້ໄຂສິນຄ້າ</h2>
            <p className="text-muted-foreground">ແຜງຄວບຄຸມ / ສິນຄ້າ / {p.name}</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => nav({ to: "/app/catalog/products" })}
            >
              <ArrowLeftIcon className="size-4" />
              ກັບຄືນ
            </Button>
            <Button onClick={onSaveGeneral} isLoading={updateProduct.isPending}>
              ບັນທຶກການປ່ຽນແປງ
            </Button>
          </div>
        </div>

        <FormRoot<GeneralFormValues>
          methods={generalForm}
          onSubmit={() => {}}
          className="space-y-6"
        >
          {/* General Information */}
          <Card className="rounded-xl border bg-card">
            <CardHeader className="pb-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="font-heading text-lg">ຂໍ້ມູນທົ່ວໄປ</CardTitle>
                <div className="flex items-center gap-2">
                  <span className="text-muted-foreground text-sm">ເປີດຂາຍ</span>
                  <RHF.Controller
                    name="isActive"
                    control={generalForm.control}
                    render={({ field }) => (
                      <Switch
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    )}
                  />
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-6 sm:grid-cols-[minmax(0,240px)_1fr]">
                <RHF.Controller
                  name="imageUrl"
                  control={generalForm.control}
                  render={({ field }) => (
                    <ProductImageUploadField
                      value={field.value ?? ""}
                      onChange={field.onChange}
                      pendingFile={pendingImageFile}
                      onFileSelect={setPendingImageFile}
                      disabled={updateProduct.isPending}
                      label="ຮູບສິນຄ້າ"
                    />
                  )}
                />
                <div className="space-y-4">
                  <FormInput
                    name="name"
                    label="ຊື່ສິນຄ້າ"
                    placeholder="ຕົວຢ່າງ: Organic Arabica Coffee Beans"
                    requiredMark
                  />
                  <FormTextarea
                    name="description"
                    label="ຄຳອະທິບາຍ"
                    placeholder="ຄຳອະທິບາຍສັ້ນໆ"
                    className="min-h-24 resize-none rounded-[12px]"
                  />
                </div>
              </div>
            </CardContent>
          </Card>
        </FormRoot>

        {/* Two columns: Variants | Modifiers */}
        <div className="mt-6 grid gap-6 lg:grid-cols-3">
          {/* Variants */}
          <Card className="rounded-xl border bg-card lg:col-span-2">
            <CardHeader className="pb-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="font-heading text-lg">
                  ຮູບແບບສິນຄ້າ
                  <span className="ml-2 font-normal text-muted-foreground">
                    {p.variants?.length ?? 0} ທັງໝົດ
                  </span>
                </CardTitle>
                <Button size="sm" onClick={openAddVariant}>
                  + ເພີ່ມຮູບແບບ
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded-lg border">
                <table className="w-full text-left text-sm">
                  <thead>
                    <tr className="border-b bg-muted/50">
                      <th className="p-2 font-medium">ຊື່</th>
                      <th className="p-2 font-medium">SKU</th>
                      <th className="p-2 font-medium">ບາໂຄດ</th>
                      <th className="p-2 font-medium">ລາຄາຕົ້ນທຶນ (LAK)</th>
                      <th className="p-2 font-medium">Gold launch</th>
                      <th className="min-w-[180px] p-2 font-medium">
                        ການດຳເນີນການ
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {(p.variants ?? []).map((v) => (
                      <tr key={v.id} className="border-b last:border-0">
                        <td className="p-2">{v.name}</td>
                        <td className="p-2 text-muted-foreground">
                          {v.sku ?? "—"}
                        </td>
                        <td className="p-2 text-muted-foreground">
                          {v.barcode ?? "—"}
                        </td>
                        <td className="p-2">
                          {formatPriceLak(v.basePriceLak)}
                        </td>
                        <td className="p-2">
                          <div className="flex flex-col gap-1">
                            <Switch
                              checked={
                                launchByVariantId.get(v.id)?.goldFreeEnabled ??
                                false
                              }
                              disabled={upsertProductLaunch.isPending}
                              onCheckedChange={(checked) => {
                                void upsertProductLaunch
                                  .mutateAsync({
                                    variantId: v.id,
                                    payload: {
                                      goldFreeEnabled: checked,
                                      launchAt: new Date().toISOString(),
                                    },
                                  })
                                  .then(() => {
                                    toast.success(
                                      checked
                                        ? "ເປີດ Gold launch free ແລ້ວ"
                                        : "ປິດ Gold launch free ແລ້ວ",
                                    );
                                  })
                                  .catch((error: unknown) => {
                                    const msg =
                                      error &&
                                      typeof (error as { message?: string })
                                        .message === "string"
                                        ? (error as { message: string }).message
                                        : "ບັນທຶກ launch ບໍ່ສຳເລັດ";
                                    toast.error(msg);
                                  });
                              }}
                            />
                            {launchByVariantId.get(v.id)?.goldFreeEnabled ? (
                              <span className="text-muted-foreground text-[10px]">
                                Gold ຊິມຟຣີ 1 ຄັ້ງ
                              </span>
                            ) : null}
                          </div>
                        </td>
                        <td className="p-2">
                          <div className="flex items-center gap-1">
                            {canEditRecipe && (
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="icon"
                                    onClick={() =>
                                      nav({
                                        to: "/app/inventory/recipes/$variantId/edit",
                                        params: { variantId: v.id },
                                      })
                                    }
                                  >
                                    <ListIcon className="size-4" />
                                  </Button>
                                </TooltipTrigger>
                                <TooltipContent>
                                  <p>ແກ້ໄຂສູດ</p>
                                </TooltipContent>
                              </Tooltip>
                            )}
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={() => openEditVariant(v)}
                                >
                                  <PencilIcon className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>ແກ້ໄຂ variant</p>
                              </TooltipContent>
                            </Tooltip>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <Button
                                  type="button"
                                  variant="ghost"
                                  size="icon"
                                  onClick={async () => {
                                    try {
                                      const ok = await confirm({
                                        title: "ລົບ variant",
                                        description:
                                          "ທ່ານແນ່ໃຈບໍ່ວ່າຈະລົບ variant ນີ້?",
                                        actionText: "ລົບ",
                                        ActionProps: { variant: "destructive" },
                                      });
                                      if (!ok) return;
                                      await removeVariant.mutateAsync(v.id);
                                      toast.success("ລົບ variant ສຳເລັດ");
                                    } catch (e: unknown) {
                                      const msg =
                                        e &&
                                        typeof (e as { message?: string })
                                          .message === "string"
                                          ? (e as { message: string }).message
                                          : "ບໍ່ສາມາດລົບ variant ສຸດທິໄດ້";
                                      toast.error(msg);
                                    }
                                  }}
                                  disabled={removeVariant.isPending}
                                >
                                  <Trash2Icon className="size-4" />
                                </Button>
                              </TooltipTrigger>
                              <TooltipContent>
                                <p>ລົບ variant</p>
                              </TooltipContent>
                            </Tooltip>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>

          {/* Modifiers */}
          <Card className="rounded-xl border bg-card">
            <CardHeader className="pb-4">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="font-heading text-lg">
                  ຕົວເລືອກເພີ່ມ
                </CardTitle>
                <Button size="sm" onClick={() => setLinkModifierOpen(true)}>
                  ຜູກຊຸດ
                </Button>
              </div>
              <p className="text-muted-foreground text-sm">
                ຈັດການຕົວເລືອກເພີ່ມເຕີມ ຫຼື ຊຸດປັບແຕ່ງສຳລັບສິນຄ້ານີ້.
              </p>
            </CardHeader>
            <CardContent className="space-y-2">
              {(p.linkedModifiers ?? []).map((m, idx) => (
                <ModifierLinkCard
                  key={m.id}
                  modifier={m}
                  onRemove={() => removeModifierLink.mutate(m.modifierId)}
                  onMoveUp={idx > 0 ? () => moveModifier(idx, -1) : undefined}
                  onMoveDown={
                    idx < (p.linkedModifiers?.length ?? 0) - 1
                      ? () => moveModifier(idx, 1)
                      : undefined
                  }
                  removing={removeModifierLink.isPending}
                />
              ))}
              <button
                type="button"
                onClick={() => setLinkModifierOpen(true)}
                className="flex w-full items-center justify-center gap-2 rounded-lg border border-input border-dashed py-4 text-muted-foreground hover:bg-muted/50"
              >
                + ຜູກຊຸດຕົວເລືອກ
              </button>
              <p className="text-muted-foreground text-xs italic">
                ໃຊ້ປຸ່ມຂຶ້ນ/ລົງເພື່ອປ່ຽນລຳດັບການເປີດໃຫ້ລູກຄ້າເຫັນ.
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Archive Product */}
        <Card className="mt-6 rounded-xl border border-destructive/30 bg-card">
          <CardHeader className="flex flex-wrap items-center justify-between gap-2">
            <div>
              <CardTitle className="font-heading text-destructive text-lg">
                ເກັບສິນຄ້າ
              </CardTitle>
              <p className="text-muted-foreground text-sm">
                ການເກັບສິນຄ້ານີ້ຈະລຶບອອກຈາກທຸລະກັນທຸລະການ ແລະ ສາງທີ່ໃຊ້ງານທັງໝົດ.
              </p>
            </div>
            <Button
              variant="destructive"
              onClick={async () => {
                const ok = await confirm({
                  title: "ປິດການຂາຍສິນຄ້າ",
                  description: "ທ່ານແນ່ໃຈບໍ່ວ່າຈະປິດການຂາຍສິນຄ້ານີ້?",
                  actionText: "ປິດການຂາຍ",
                  ActionProps: { variant: "destructive" },
                });
                if (!ok) return;
                await updateProduct.mutateAsync({
                  type: p.type,
                  name: p.name,
                  description: p.description ?? null,
                  imageUrl: p.imageUrl ?? null,
                  isActive: false,
                });
                toast.success("ປິດການຂາຍສິນຄ້າສຳເລັດ");
              }}
              disabled={updateProduct.isPending}
            >
              ປິດການຂາຍສິນຄ້າ
            </Button>
          </CardHeader>
        </Card>
      </Main>

      <VariantFormModal
        open={variantModal.open}
        onOpenChange={(open) => setVariantModal((prev) => ({ ...prev, open }))}
        variant={variantModal.variant}
        onSubmit={async (payload) => {
          if (variantModal.variant) {
            await updateVariant.mutateAsync(payload);
            toast.success("ແກ້ໄຂ variant ສຳເລັດ");
          } else {
            await createVariant.mutateAsync(payload);
            toast.success("ເພີ່ມ variant ສຳເລັດ");
          }
          setVariantModal({ open: false, variant: null });
          setEditingVariantId("");
        }}
        submitting={createVariant.isPending || updateVariant.isPending}
      />

      <LinkModifierSetModal
        open={linkModifierOpen}
        onOpenChange={setLinkModifierOpen}
        allModifiers={modifiersQuery.data ?? []}
        linkedModifiers={p.linkedModifiers ?? []}
        onSelect={async (modifierId) => {
          await addModifierLink.mutateAsync(modifierId);
        }}
        submitting={addModifierLink.isPending}
      />
    </>
  );
}

function ModifierLinkCard({
  modifier,
  onRemove,
  onMoveUp,
  onMoveDown,
  removing,
}: {
  modifier: LinkedModifierRow;
  onRemove: () => void;
  onMoveUp?: () => void;
  onMoveDown?: () => void;
  removing: boolean;
}) {
  const choiceLabel = modifier.singleChoice ? "ເລືອກໄດ້ອັນດຽວ" : "ເລືອກໄດ້ຫຼາຍອັນ";
  return (
    <div className="flex items-center gap-2 rounded-lg border bg-muted/20 p-3">
      <div className="flex flex-col">
        {onMoveUp && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onMoveUp}
          >
            ↑
          </Button>
        )}
        {onMoveDown && (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-6 w-6"
            onClick={onMoveDown}
          >
            ↓
          </Button>
        )}
        {!onMoveUp && !onMoveDown && (
          <GripVerticalIcon className="size-4 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-medium text-sm">{modifier.modifierName}</p>
        <p className="text-muted-foreground text-xs">
          {modifier.optionCount} ຕົວເລືອກ • {choiceLabel}
        </p>
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        disabled={removing}
        aria-label="ລົບ"
      >
        ×
      </Button>
    </div>
  );
}
